import { ICharacter } from "@next-platform/client-common-core";

import { FilterKeys, SortOptions } from "@/components/type";

type FilterableItem = {
  id?: string;
  name?: string;
  visibility?: string;
  moderationReport?: {
    moderation_status?: string;
  };
  moderationReportStatus?: string;
  updatedAt?: number;
  rating_score?: number;
};

export const filterAndSortResults = <T extends FilterableItem = ICharacter>(
  data: T[] = [],
  searchValue: string = "",
  sortValue: string = "",
  filterKey?: string,
  filterValue?: string,
) => {
  let results = Array.isArray(data) ? [...data] : [];
  // Apply search
  if (searchValue) {
    results = results.filter(
      (bot: T) => bot?.name?.toLowerCase()?.includes(searchValue.toLowerCase()) || bot?.id?.toLowerCase()?.includes(searchValue.toLowerCase()),
    );
  }

  // Apply filtering
  if (filterKey && filterKey !== FilterKeys.ALL) {
    const filterMapping = {
      [FilterKeys.VISIBILITY]: (bot: T) => bot.visibility === filterValue,
      [FilterKeys.MODERATION_STATUS]: (bot: T) => bot?.moderationReport?.moderation_status === filterValue,
      [FilterKeys.MODERATION_REPORT_STATUS]: (bot: T) => bot?.moderationReportStatus === filterValue,
    };

    if (filterMapping[filterKey as keyof typeof filterMapping]) {
      results = results.filter(filterMapping[filterKey as keyof typeof filterMapping]);
    }
  }

  // Apply sorting
  const sortMapping = {
    [SortOptions.LATEST]: (a: T, b: T) => {
      // Handle cases where updatedAt might be undefined
      if (!a.updatedAt) return 1;
      if (!b.updatedAt) return -1;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    },
    [SortOptions.OLDEST]: (a: T, b: T) => {
      // Handle cases where updatedAt might be undefined
      if (!a.updatedAt) return -1;
      if (!b.updatedAt) return 1;
      return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
    },
    [SortOptions.TOP_RATED]: (a: T, b: T) => (b.rating_score || 0) - (a.rating_score || 0),
    [SortOptions.NAME]: (a: T, b: T) => (a.name || "").localeCompare(b.name || ""),
  };

  if (sortValue && sortMapping[sortValue as keyof typeof sortMapping]) {
    results.sort(sortMapping[sortValue as keyof typeof sortMapping]);
  }

  return results;
};
